import { Controller } from "@hotwired/stimulus"

// Admin panel mobile drawer.
//
// Wraps the admin layout root. Toggles the sidebar via `data-state` on a
// "sidebar" target, shows/hides the overlay, locks body scroll, closes on
// ESC, and closes on any nav <a> click inside the sidebar so taps that
// navigate also dismiss the drawer.
//
// Active only on viewports < 768px (md breakpoint); on desktop the
// hamburger is hidden via Tailwind utilities and toggle calls become
// no-ops visually (the sidebar is permanently visible via
// `md:translate-x-0` on the SidebarComponent root).
export default class extends Controller {
  static targets = ["sidebar", "overlay"]

  connect() {
    this._onKeydown = this._onKeydown.bind(this)
    this._onNavClick = this._onNavClick.bind(this)
    document.addEventListener("keydown", this._onKeydown)
    this._bindNavLinks()
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    this._unbindNavLinks()
    this._unlockBodyScroll()
  }

  toggle() {
    if (this._isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (!this.hasSidebarTarget) return
    this.sidebarTarget.dataset.state = "open"
    if (this.hasOverlayTarget) {
      this.overlayTarget.dataset.state = "open"
      this.overlayTarget.removeAttribute("hidden")
    }
    this._lockBodyScroll()
  }

  close() {
    if (this.hasSidebarTarget) delete this.sidebarTarget.dataset.state
    if (this.hasOverlayTarget) {
      delete this.overlayTarget.dataset.state
      this.overlayTarget.setAttribute("hidden", "")
    }
    this._unlockBodyScroll()
  }

  _isOpen() {
    return this.hasSidebarTarget && this.sidebarTarget.dataset.state === "open"
  }

  _onKeydown(event) {
    if (event.key === "Escape" && this._isOpen()) this.close()
  }

  _onNavClick() {
    if (this._isOpen()) this.close()
  }

  _bindNavLinks() {
    this._navLinks().forEach((link) => {
      link.addEventListener("click", this._onNavClick)
    })
  }

  _unbindNavLinks() {
    this._navLinks().forEach((link) => {
      link.removeEventListener("click", this._onNavClick)
    })
  }

  _navLinks() {
    if (!this.hasSidebarTarget) return []
    const nav = this.sidebarTarget.querySelector("[data-admin-sidebar-nav]")
    return nav ? Array.from(nav.querySelectorAll("a")) : []
  }

  // Lock body scroll while the drawer is open so scrolling the drawer
  // doesn't bleed through to the main content underneath. Stores the
  // prior overflow value so we restore exactly what was there (avoids
  // stomping on a different controller that may have already locked).
  _lockBodyScroll() {
    if (typeof document === "undefined") return
    if (this._previousBodyOverflow !== undefined) return
    this._previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
  }

  _unlockBodyScroll() {
    if (typeof document === "undefined") return
    if (this._previousBodyOverflow === undefined) return
    document.body.style.overflow = this._previousBodyOverflow
    this._previousBodyOverflow = undefined
  }
}
